import React from 'react';
import { FaCheckCircle, FaRegCalendarPlus } from 'react-icons/fa';
import { MdMoreHoriz } from 'react-icons/md';
import { BsStopwatch } from 'react-icons/bs';
import classNames from 'classnames';

import { format } from 'legacy/common/utils/datetime';
import { UserTicketDto } from '../../../../types';
import { useGetMe } from 'legacy/user/hooks';
import AddToCalendar from '../../../AddToCalendar';
import { toCalendarEvent } from '../../../../helpers';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { copyToClipboard } from 'legacy/common/utils/clipboard';
import * as toast from 'legacy/common/utils/toast';
import TicketHolder from '../TicketHolder';
import generateTransferUrl from '../../helpers/generateTransferUrl';

type Props = {
  ticket: UserTicketDto;
  isTransfer?: boolean;
  setSelectedTicket: (ticket: UserTicketDto) => void;
};

const TicketCard = ({ ticket, isTransfer, setSelectedTicket }: Props) => {
  const { data: user } = useGetMe();
  const isTicketOwner = ticket?.owner.id === user?._id;

  const onOptionsClick = () => {
    setSelectedTicket(ticket);
  };

  const onTransferClick = () => {
    copyToClipboard(generateTransferUrl(ticket.id));
    toast.success('Link Copied! Make a new memory-- share it with a friend.');
  };

  return (
    <div className="flex flex-col bg-white rounded-2xl p-5 shadow-[0_3px_12px_10px_rgba(111,123,235,0.1)]">
      <div className="flex items-center">
        <TicketHolder
          ticket={ticket}
          isTransfer={isTransfer}
          isTicketOwner={isTicketOwner}
        />
        {isTicketOwner && (
          <button type="button" onClick={onOptionsClick} className="p-2 pr-0">
            <MdMoreHoriz size={24} />
          </button>
        )}
      </div>
      <div className="my-4 relative flex justify-center w-56 h-56 self-center">
        <img className={classNames('w-56 h-56')} src={ticket?.qrCodePhotoUrl} />
        {ticket?.isScanned ? (
          <div className="absolute inset-0 flex justify-center items-center">
            <div className="p-1 rounded-full bg-white">
              <FaCheckCircle className="text-4xl text-green-500" />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex justify-center items-center">
            <img className="w-16 h-16" src="/img/logos/light_ball.png" />
          </div>
        )}
      </div>
      {ticket?.isScanned && ticket?.scanDate && (
        <div className="mb-1 text-center text-gray-400">
          Last scan: {format(ticket?.scanDate)}
        </div>
      )}
      <h2 className="text-xl text-gray-700">{ticket.event.title}</h2>
      <div className="flex items-center justify-between pt-4 text-sm text-gray-500">
        <div>
          <div className="flex items-center">
            <BsStopwatch className="mr-1" />
            {format(ticket.event.startDate)}
          </div>
          <p className="pt-1 text-sm text-gray-400">{ticket.event.address}</p>
        </div>
        <div className="flex relative">
          <AddToCalendar
            position="right"
            label={
              <FaRegCalendarPlus
                size={18}
                className="ml-3 mr-1 text-primary-500 cursor-pointer"
              />
            }
            event={toCalendarEvent({
              title: ticket.event.title,
              startDate: ticket.event.startDate,
              endDate: ticket.event.endDate,
              location: ticket.event.address
            })}
          />
        </div>
      </div>
      {!isTransfer && isTicketOwner && (
        <div className="flex items-center justify-between pt-4">
          <Button onClick={onTransferClick} type={ButtonType.Inverted}>
            Transfer ticket
          </Button>
        </div>
      )}
    </div>
  );
};

export default TicketCard;
